import type { ReactElement } from 'react';

import { HighlightText } from '@bestseller-bit/frontend-community.components.highlight-text';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { useTheme } from '@mui/material/styles';
import { TruncatedText } from '../../TruncatedText';

interface ColourLabelProps {
    /** The colour name to display, including any style variant */
    colourName: string;
    /** Hex code for the swatch; falls back to a transparent (outlined) dot */
    hexCode?: string | null;
    /** Search term to highlight within the colour name */
    highlight?: string;
}

/**
 * A colour swatch dot next to its colour name, used in table rows. The name is
 * truncated with an ellipsis and reveals its full text in a tooltip when cut off.
 * @requires ThemeProvider - Needs Material-UI theme context
 */
const ColourLabel = ({
    colourName,
    hexCode,
    highlight,
}: ColourLabelProps): ReactElement => {
    const { palette } = useTheme();

    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center', minWidth: 0 }}
            useFlexGap
        >
            {hexCode && (
                <Box
                    sx={{
                        width: '24px',
                        aspectRatio: '1 / 1',
                        flexShrink: 0,
                        backgroundColor: hexCode,
                        border: `1px solid ${palette.divider}`,
                    }}
                />
            )}

            <TruncatedText
                variant="captions.default"
                tooltipTitle={colourName}
            >
                <HighlightText
                    text={colourName}
                    highlight={highlight}
                />
            </TruncatedText>
        </Stack>
    );
};

export { ColourLabel };
